import * as yup from 'yup';

import { ACTIVE_COMPANY_TYPES } from '../../../utils/mock';
import { RowActionsDropdown } from '../../common/table/action-dropdown/RowActionsDropdown';
import { ProfileImage } from '../../profile-image/ProfileImage';

export const getClientColumnDefinition = (
  activate,
  onEdit,
  onView,
  onDelete,
  onActivate,
  onDeactivate
) => [
  {
    id: 'initials',
    accessorKey: 'name',
    header: '',
    size: 64,
    minSize: 64,
    maxSize: 64,
    disableExport: true,
    filterFn: 'tableFilterFn',
    cell: ({ row }) => (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
      >
        <ProfileImage width={40} height={40} profile={{ ...row.original }} />
      </div>
    )
  },
  {
    id: 'clientId',
    accessorKey: 'clientId',
    header: 'Client ID',
    size: 100,
    minSize: 100,
    maxSize: 100,
    filterFn: 'tableFilterFn',
    cell: (info) => <span>{info.getValue()}</span>
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    size: 200,
    minSize: 200,
    filterFn: 'tableFilterFn',
    cell: (info) => <span>{info.getValue()}</span>
  },
  {
    id: 'active',
    accessorKey: 'active',
    size: 100,
    minSize: 100,
    maxSize: 100,
    header: 'Status',
    filterFn: 'tableFilterFn',
    cell: ({ row }) => <span>{row.original.active ? 'Active' : 'Inactive'}</span>
  },
  {
    id: 'type',
    accessorKey: 'type',
    size: 200,
    minSize: 200,
    maxSize: 100,
    header: 'Type',
    filterFn: 'tableFilterFn',
    cell: ({ row }) => (
      <span>{ACTIVE_COMPANY_TYPES.find((t) => t.value === row.original.type).label}</span>
    )
  },
  {
    id: 'email',
    accessorKey: 'email',
    size: 200,
    minSize: 200,
    header: 'Email',
    filterFn: 'tableFilterFn'
  },
  {
    id: 'actions',
    header: '',
    size: 40,
    minSize: 40,
    maxSize: 40,
    disableExport: true,
    enableSorting: false,
    cell: ({ row }) => (
      <RowActionsDropdown
        row={row}
        actions={[
          { label: 'View', value: 'view', onSelect: (data) => (onView ? onView(data) : null) },
          { label: 'Edit', value: 'edit', onSelect: (data) => (onEdit ? onEdit(data) : null) },
          {
            label: 'Delete',
            value: 'delete',
            onSelect: (data) => (onDelete ? onDelete(data) : null)
          },
          {
            label: activate ? 'Activate' : 'Deactivate',
            value: activate ? 'activate' : 'deactivate',
            onSelect: (data) =>
              activate
                ? onActivate
                  ? onActivate(data)
                  : null
                : onDeactivate
                  ? onDeactivate(data)
                  : null
          }
        ]}
      />
    )
  }
];

export const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const TABS_SOLE_PROPRIETOR = [
  {
    label: 'Personal Details',
    fields: [
      {
        type: 'input',
        name: 'clientId',
        editOnly: true,
        display: true,
        colSpan: 2,
        label: 'Client ID'
      },
      {
        type: 'select',
        name: 'type',
        label: 'Type',
        options: ACTIVE_COMPANY_TYPES,
        required: true,
        validation: yup.string().required('Type is required')
      },
      {
        type: 'input',
        name: 'name',
        label: 'Trading Name',
        required: true,
        validation: yup.string().required('Trading Name is required')
      },
      {
        type: 'input',
        name: 'firstName',
        label: 'First Name',
        required: true,
        validation: yup.string().required('First Name is required')
      },
      {
        type: 'input',
        name: 'lastName',
        label: 'Last Name',
        required: true,
        validation: yup.string().required('Last Name is required')
      },
      {
        type: 'input',
        name: 'idNumber',
        label: 'ID Number',
        required: true,
        validation: yup.string().required('ID Number is required')
      },
      {
        type: 'empty'
      },
      {
        type: 'input',
        name: 'contactNumber',
        label: 'Contact Number',
        required: true,
        validation: yup.string().required('Contact Number is required')
      },
      {
        type: 'input',
        name: 'email',
        label: 'Email',
        required: true,
        validation: yup.string().email().required('Email is required')
      },
      {
        type: 'textarea',
        name: 'physicalAddress',
        label: 'Physical Address',
        required: true,
        colSpan: 2,
        validation: yup.string().required('Physical Address is required')
      },
      {
        type: 'textarea',
        name: 'postalAddress',
        label: 'Postal Address',
        required: true,
        linkedKey: 'physicalAddress',
        linkedLabel: 'Same as Physical Address',
        colSpan: 2,
        validation: yup.string().required('Postal Address is required')
      }
    ]
  },
  {
    label: 'Business Information',
    fields: [
      {
        type: 'input',
        name: 'industry',
        label: 'Industry',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'input',
        name: 'itr',
        label: 'Personal Income Tax Number (ITR)',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'yesno',
        name: 'provisionalTaxStatus',
        label: 'Provisional Tax Status',
        required: true,
        defaultValue: false,
        validation: yup.boolean().required('Please select Yes or No')
      },
      {
        type: 'date',
        name: 'commencement',
        label: 'Date of Business Commencement',
        required: true,
        placeholder: 'Select a date',
        validation: yup.date().typeError('Date must be a valid date').required('Date is required')
      }
    ]
  },
  {
    label: 'Registration & Compliance',
    fields: [
      {
        type: 'input',
        name: 'vatRegistrationNumber',
        label: 'VAT Registration Number',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'input',
        name: 'uifAndSdlRegistration',
        label: 'UIF & SDL Registration',
        validation: yup.string().nullable().optional()
      }
    ]
  },
  {
    label: 'Banking Details',
    fields: [
      {
        type: 'input',
        name: 'bankName',
        label: 'Bank Name',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'input',
        name: 'accountHolderName',
        label: 'Account Holder Name',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'input',
        name: 'accountNumber',
        label: 'Account Number',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'input',
        name: 'accountType',
        label: 'Account Type',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'input',
        name: 'branchCode',
        label: 'Branch Code',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'empty'
      }
    ]
  }
];

const TABS_PARTNERSHIP = [
  {
    label: 'Personal Details',
    fields: [
      {
        type: 'input',
        name: 'clientId',
        editOnly: true,
        display: true,
        colSpan: 2,
        label: 'Client ID'
      },
      {
        type: 'select',
        name: 'type',
        label: 'Type',
        options: ACTIVE_COMPANY_TYPES,
        required: true,
        validation: yup.string().required('Type is required')
      },
      {
        type: 'input',
        name: 'name',
        label: 'Trading Name',
        required: true,
        validation: yup.string().required('Trading Name is required')
      },
      {
        type: 'input',
        name: 'email',
        label: 'Email',
        required: true,
        validation: yup.string().email().required('Email is required')
      }
    ]
  }
];

const TABS = [
  {
    key: 'sole-proprietor',
    tabs: TABS_SOLE_PROPRIETOR
  },
  {
    key: 'partnership',
    tabs: TABS_PARTNERSHIP
  }
];

export const getTabsByKey = (key) => {
  const match = TABS.find((entry) => entry.key === key);
  return match?.tabs || [];
};
